/**
 * SignalList - the "Data Signals" overview tab.
 *
 * Reimagined as a visual atlas of the workspace's signals rather than
 * a wide table. Each signal becomes a richly visual card with its
 * own distribution chart, relevance gauge, and quality
 * chips. Above the grid sits a hero KPI strip and a collapsible
 * filter / sort toolbar.
 *
 * Redux contract is unchanged - all the existing actions, polling
 * loops, date-range auto-detection, and delete dialog plumbing are
 * preserved.
 */
import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import useRealtimeRefresh, { eventPrefix } from '../realtime/useRealtimeRefresh';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Autocomplete, Box, Button, CircularProgress, Chip, Dialog, DialogActions,
  DialogContent, DialogContentText, DialogTitle, FormControl, InputLabel,
  MenuItem, Pagination, Select, Stack, TextField, ToggleButton,
  ToggleButtonGroup, Tooltip, Typography,
  alpha, useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CheckIcon from '@mui/icons-material/Check';
import CancelIcon from '@mui/icons-material/Cancel';
import InsightsIcon from '@mui/icons-material/Insights';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import GridViewIcon from '@mui/icons-material/GridView';
import AppsIcon from '@mui/icons-material/Apps';
import ViewListIcon from '@mui/icons-material/ViewList';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import LabelOffOutlinedIcon from '@mui/icons-material/LabelOffOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloseIcon from '@mui/icons-material/Close';
import StraightenOutlinedIcon from '@mui/icons-material/StraightenOutlined';

import { fetchRequest as signalFetchAll } from '../redux/actions/signals/list';
import { fetchRequest as overviewFetch } from '../redux/actions/signals/overview';
import {
  fetchRequest as signalDelete,
  clearMarketplaceConflict as clearSignalDeleteMarketplaceConflictAction,
} from '../redux/actions/signals/delete';
import { fetchRequest as signalGroupsFetch } from '../redux/actions/signals/groups';
import { fetchRequest as signalGroupAssign } from '../redux/actions/signals/groupAssign';
import {
  fetchRequest as signalDeleteBulk,
  clearMarketplaceConflict as clearBulkDeleteConflictAction,
} from '../redux/actions/signals/deleteBulk';
import {
  fetchRequest as eventStreamDelete,
  clearState as clearEventStreamDelete,
} from '../redux/actions/events/deleteStream';
import { fetchRequest as signalRelevance } from '../redux/actions/signals/relevance';
import { fetchRequest as signalLatestAnalysisFetch } from '../redux/actions/signals/latestAnalysis';
import {
  fetchRequest as signalDisplayNameRequest,
  clearState as signalDisplayNameClear,
} from '../redux/actions/signals/displayName';
import {
  fetchRequest as signalAssignUnitRequest,
  clearState as signalAssignUnitClear,
} from '../redux/actions/signals/assignUnit';
import {
  cancelSubscribe, fetchPublishedSignals, fetchSellerStatus,
} from '../redux/actions/marketplace/listings';
import { CMButtonError, CMButtonSuccess } from './styledComponents';

import ListingToolbar from '../components/common/ListingToolbar';
import SignalCard, {
  familyOf, hasAnomalies,
} from '../components/signals/SignalCard';
import CompactSignalCard from '../components/signals/CompactSignalCard';
import SignalsRowsTable from '../components/signals/SignalsRowsTable';
import SignalDetailModal from '../components/signals/SignalDetailModal';
import SignalsKpiStrip from '../components/signals/SignalsKpiStrip';
import SignalsOverviewChart from '../components/signals/SignalsOverviewChart';
import PublishListingDialog from '../components/marketplace/PublishListingDialog';
import ManagePublishedDialog from '../components/signals/ManagePublishedDialog';
import { invalidateDisplayNames } from '../utils/displayNames';
import { UNIT_PRESET_OPTIONS, invalidateSignalUnits } from '../utils/units';

// Persisted view-mode preference. Stored under one key so the user's
// choice survives reloads / new sessions / workspace switches.
// ``.v2`` — the default changed to the rows (table) view; bumping the
// key resets previously persisted prefs once, then keeps persisting.
const VIEW_MODE_STORAGE_KEY = 'predictAI.signals.viewMode.v2';
const VIEW_MODE_OPTIONS = ['compact', 'grid', 'rows'];
const DEFAULT_VIEW_MODE = 'rows';

const readStoredViewMode = () => {
  try {
    const v = window.localStorage.getItem(VIEW_MODE_STORAGE_KEY);
    return VIEW_MODE_OPTIONS.includes(v) ? v : DEFAULT_VIEW_MODE;
  } catch (_e) {
    return DEFAULT_VIEW_MODE;
  }
};

const persistViewMode = (mode) => {
  try {
    window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
  } catch (_e) {
    /* localStorage unavailable (private mode) — silently ignore */
  }
};

// ---- helpers --------------------------------------------------------------

const FAMILY_FILTER_OPTIONS = [
  { value: 'event',       label: 'Event' },
  { value: 'numeric',     label: 'Numeric' },
  { value: 'categorical', label: 'Categorical' },
  { value: 'binary',      label: 'Binary' },
  { value: 'time',        label: 'Time' },
  { value: 'text',        label: 'Text' },
  { value: 'id',          label: 'Identifier' },
  { value: 'spatial',     label: 'Spatial' },
  { value: 'mixed',       label: 'Mixed' },
];

const QUALITY_FILTER_OPTIONS = [
  { value: 'patterned',     label: 'Patterned',      color: 'success' },
  { value: 'anomalous',     label: 'Anomalous',      color: 'error'   },
  { value: 'high_relevance',label: 'High Relevance', color: 'warning' },
];

// Sort options. ``relevance``, ``records``, ``recency``, ``name``,
// ``published`` and ``unpublished`` are now resolved on the BE so the
// page renders pre-sorted (no FE-only re-arrange after the relevance
// payload arrives separately). ``anomalies`` is still computed FE-side
// from the per-signal flags so the sort runs on the current page only.
const SORT_OPTIONS = [
  { value: 'relevance',   label: 'Relevance (high → low)',   beSort: 'relevance' },
  { value: 'records',     label: 'Records (high → low)',     beSort: 'records' },
  { value: 'recency',     label: 'Most recent first',        beSort: 'recency' },
  { value: 'anomalies',   label: 'Anomalies first' },
  { value: 'name',        label: 'Name (A → Z)',             beSort: 'name', beOrder: 'asc' },
  { value: 'published',   label: 'Published first',          beSort: 'published' },
  { value: 'unpublished', label: 'Unpublished first',        beSort: 'unpublished' },
];

// Marketplace published-status chips. The default is "all" (no chip
// selected). Clicking either chip activates the corresponding
// published_filter; clicking the same chip again clears back to "all".
// Only one can be active at a time (they're mutually exclusive).
const PUBLISHED_FILTER_OPTIONS = [
  { value: 'published',   label: 'Published only',   color: 'success' },
  { value: 'unpublished', label: 'Unpublished only', color: 'primary' },
];

// ---- skeleton -------------------------------------------------------------
//
// One skeleton variant per view mode. The shimmer mirrors the saved
// layout (column count, card height, table-vs-grid) so the loading
// state doesn't visually "jump" the moment real data lands.

const SkeletonCard = () => {
  const theme = useTheme();
  return (
    <Box sx={{
      height: 280,
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      bgcolor: theme.palette.background.paper,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Box sx={{
        height: 4,
        bgcolor: alpha(theme.palette.text.primary, 0.08),
      }} />
      <Box sx={{ p: 1.75, flex: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Box sx={{ height: 16, width: '60%', bgcolor: alpha(theme.palette.text.primary, 0.06), borderRadius: 0.5 }} />
        <Box sx={{ height: 12, width: '40%', bgcolor: alpha(theme.palette.text.primary, 0.04), borderRadius: 0.5 }} />
        <Box sx={{ flex: 1, mt: 1, bgcolor: alpha(theme.palette.text.primary, 0.03), borderRadius: 1 }} />
      </Box>
    </Box>
  );
};

// Tight skeleton matching CompactSignalCard's footprint (~124px tall,
// header strip + sparkline placeholder + footer strip).
const SkeletonCompactCard = () => {
  const theme = useTheme();
  return (
    <Box sx={{
      minHeight: 124,
      borderRadius: 1.25,
      border: `1px solid ${alpha(theme.palette.divider, 0.18)}`,
      bgcolor: theme.palette.background.paper,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    }}>
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        px: 1, pt: 0.75, pb: 0.25, minHeight: 22,
      }}>
        <Box sx={{ width: 16, height: 16, borderRadius: 0.5,
                   bgcolor: alpha(theme.palette.text.primary, 0.08) }} />
        <Box sx={{ height: 10, flex: 1, borderRadius: 0.5,
                   bgcolor: alpha(theme.palette.text.primary, 0.06) }} />
      </Box>
      <Box sx={{ flex: 1, mx: 0.5,
                 bgcolor: alpha(theme.palette.text.primary, 0.03),
                 borderRadius: 0.75 }} />
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 0.5,
        px: 1, pb: 0.6, pt: 0.5,
      }}>
        <Box sx={{ height: 8, width: 32, borderRadius: 0.5,
                   bgcolor: alpha(theme.palette.text.primary, 0.06) }} />
        <Box sx={{ height: 8, width: 28, borderRadius: 0.5,
                   bgcolor: alpha(theme.palette.text.primary, 0.04) }} />
        <Box sx={{ flex: 1 }} />
        <Box sx={{ height: 8, width: 24, borderRadius: 0.5,
                   bgcolor: alpha(theme.palette.text.primary, 0.06) }} />
      </Box>
    </Box>
  );
};

// Table-shaped skeleton mirroring SignalsRowsTable: a header strip
// plus N row strips with column-shaped placeholders. We approximate
// the column widths from the table's actual columns so the shimmer
// reads as the same shape.
const SkeletonRowsTable = ({ rows = 8 }) => {
  const theme = useTheme();
  const cellBg = alpha(theme.palette.text.primary, 0.06);
  const lighterBg = alpha(theme.palette.text.primary, 0.04);
  const headerBg = theme.palette.mode === 'dark'
    ? alpha(theme.palette.background.default, 0.6)
    : alpha(theme.palette.text.primary, 0.04);
  // (relative width, opacity tier) per column - matches
  // SignalsRowsTable's columns array.
  const cols = [
    { w: 36,  t: 'l' }, // select
    { w: 230, t: 'h' }, // signal
    { w: 100, t: 'l' }, // group
    { w: 76,  t: 'l' }, // flags
    { w: 78,  t: 'h' }, // records
    { w: 95,  t: 'l' }, // span
    { w: 88,  t: 'h' }, // last value
    { w: 72,  t: 'l' }, // trend
    { w: 140, t: 'l' }, // sparkline
    { w: 112, t: 'h' }, // relevance
    { w: 88,  t: 'l' }, // actions
  ];
  return (
    <Box sx={{
      borderRadius: 2,
      border: `1px solid ${theme.palette.divider}`,
      bgcolor: theme.palette.background.paper,
      overflow: 'hidden',
    }}>
      {/* Header */}
      <Box sx={{
        display: 'flex', gap: 1.5, px: 2, py: 1.25,
        borderBottom: `1px solid ${theme.palette.divider}`,
        bgcolor: headerBg,
      }}>
        {cols.map((c, i) => (
          <Box key={`h${i}`} sx={{
            width: c.w, height: 8, borderRadius: 0.5,
            bgcolor: alpha(theme.palette.text.primary, 0.08),
          }} />
        ))}
      </Box>
      {/* Rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <Box key={`r${r}`} sx={{
          display: 'flex', gap: 1.5, px: 2, py: 1.25,
          borderBottom: r === rows - 1
            ? 0 : `1px solid ${theme.palette.divider}`,
          alignItems: 'center',
        }}>
          {cols.map((c, i) => (
            <Box key={`r${r}c${i}`} sx={{
              width: c.w,
              height: 12,
              borderRadius: 0.5,
              bgcolor: c.t === 'h' ? cellBg : lighterBg,
            }} />
          ))}
        </Box>
      ))}
    </Box>
  );
};

// ---- component ------------------------------------------------------------

const SignalList = React.memo(({
  signalFetchAll, payload, isLoading, error, query,
  deleteSignal, deletedSignal, deletedSignalError,
  deleteEventStream, deletedEventStream, clearEventStreamDelete, refreshOverview,
  signalDeleteMarketplaceConflict, clearSignalDeleteMarketplaceConflict,
  relevanceSignal, relevancePayload, relevanceLoading,
  signalLatestAnalysisFetch, analysisData,
  cancelMarketplaceSubscription,
  fetchPublishedSignals, fetchMarketplaceSellerStatus, publishedSignals,
  marketplaceMutations,
  onRecordEvent,
  // Group management + bulk actions
  fetchSignalGroups, groupsPayload,
  assignGroup, groupAssignResult, groupAssignLoading,
  bulkDelete, bulkDeleteResult, bulkDeleteLoading,
  bulkDeleteConflict, clearBulkDeleteConflict,
  // Rename (display name) + measurement unit assignment
  saveDisplayName, renameSaving, renameResult, renameSaveError, clearRenameState,
  saveUnitAssign, unitSaving, unitResult, unitSaveError, clearUnitState,
}) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const [page, setPage] = useState(payload?.pagination?.current_page || 1);
  const count = Math.max(1, Math.ceil(payload?.pagination?.total_pages || 1));
  const [signals, setSignals] = useState([]);

  // Filter / sort state. ``publishedFilter`` is sent to the BE so the
  // page query and the activeFilterCount stay in sync without the FE
  // having to know about each signal's published status to filter on
  // it client-side.
  const [search, setSearch] = useState('');
  const [familyFilter, setFamilyFilter] = useState([]);
  const [qualityFilter, setQualityFilter] = useState([]);
  const [publishedFilter, setPublishedFilter] = useState('all');
  const [sortBy, setSortBy] = useState('relevance');

  // View mode: 'compact' (6/row tight cards), 'grid' (default 3/row
  // SignalCard), or 'rows' (DataTable like Trainings). Persisted in
  // localStorage so refresh / new tab keeps the choice.
  const [viewMode, setViewMode] = useState(() => readStoredViewMode());
  useEffect(() => { persistViewMode(viewMode); }, [viewMode]);

  // Date filter (preserved from the old page)
  const [startDateTime, setStartDateTime] = useState('');
  const [endDateTime, setEndDateTime] = useState('');
  const [appliedStartDateTime, setAppliedStartDateTime] = useState('');
  const [appliedEndDateTime, setAppliedEndDateTime] = useState('');

  // Delete dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [signalToBeDeleted, setSignalToBeDeleted] = useState(null);
  const [deleteIsEvent, setDeleteIsEvent] = useState(false);
  const [deleteStreamKey, setDeleteStreamKey] = useState(null);
  const [deleteQueue, setDeleteQueue] = useState([]);

  // ---- bulk selection + group management ---------------------------------
  // Selection is a Map keyed by row key and survives page changes so users
  // can build a cross-page batch. The value records whether the row is an
  // event stream (and which stream) at selection time — rows from other
  // pages aren't around later to ask, and event streams delete through a
  // different path than signals.
  const [selected, setSelected] = useState(() => new Map());
  const [groupFilter, setGroupFilter] = useState('');
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [groupDraft, setGroupDraft] = useState('');
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);

  const rowMeta = (row) => ({
    isEvent: row.type === 'event',
    stream: row.stream || row.key,
  });
  // Stable callbacks (functional setState only) so the rows table +
  // memoized DataTable rows never re-render because a handler identity
  // changed.
  const toggleSelect = useCallback((row) => setSelected((prev) => {
    const next = new Map(prev);
    if (next.has(row.key)) next.delete(row.key);
    else next.set(row.key, rowMeta(row));
    return next;
  }), []);
  // Header checkbox: select the whole page, or clear it if fully selected.
  const toggleSelectPage = useCallback((rows) => setSelected((prev) => {
    const next = new Map(prev);
    const allIn = rows.every((r) => next.has(r.key));
    rows.forEach((r) => (
      allIn ? next.delete(r.key) : next.set(r.key, rowMeta(r))
    ));
    return next;
  }), []);
  const clearSelection = useCallback(() => setSelected(new Map()), []);

  // Derived views of the selection: the table needs a Set for checkbox
  // state; the bulk actions need the signal/event split.
  const selectedKeySet = useMemo(
    () => new Set(selected.keys()),
    [selected],
  );
  const selectedSignalKeys = useMemo(
    () => [...selected.entries()]
      .filter(([, m]) => !m.isEvent).map(([k]) => k),
    [selected],
  );
  const selectedEventStreams = useMemo(
    () => [...selected.values()]
      .filter((m) => m.isEvent).map((m) => m.stream),
    [selected],
  );
  const selectedEventCount = selectedEventStreams.length;

  // Group names for the filter dropdown + assign dialog autocomplete.
  const groupOptions = useMemo(
    () => (groupsPayload?.groups || []).map((g) => g.name),
    [groupsPayload],
  );

  // Marketplace publish dialog (per-signal)
  const [publishTarget, setPublishTarget] = useState(null);

  // Manage-an-already-published listing dialog (Edit / Delist).
  const [managePublished, setManagePublished] = useState(null);

  // Marketplace unsubscribe confirm
  const [unsubscribeTarget, setUnsubscribeTarget] = useState(null);

  // Rename (display name) dialog. The display name is presentation-only -
  // the signal key never changes - and is used platform-wide (charts,
  // segments, trainings, forms).
  const [renameTarget, setRenameTarget] = useState(null);
  const [renameDraft, setRenameDraft] = useState('');
  const [renameError, setRenameError] = useState(null);

  const openRename = useCallback((signal) => {
    setRenameTarget(signal);
    setRenameDraft(signal.display_name || '');
    setRenameError(null);
  }, []);

  const saveRename = () => {
    if (!renameTarget || renameSaving) return;
    setRenameError(null);
    saveDisplayName(renameTarget.key, renameDraft.trim());
  };

  // Rename saved: reflect immediately in the local list (redux refetch
  // not needed - the BE enriches the next GET /signal with the same
  // name), bust the cross-surface display-name caches, close the dialog.
  useEffect(() => {
    if (!renameResult?.at || !renameTarget) return;
    if (renameResult.key !== renameTarget.key) return;
    const name = renameResult.display_name;
    setSignals((prev) => prev.map((s) => (
      s.key === renameTarget.key
        ? { ...s, display_name: name || undefined }
        : s
    )));
    // Other surfaces (discovery, models, relationship graphs) cache the
    // workspace name map — make them refetch on next mount.
    invalidateDisplayNames();
    setRenameTarget(null);
    clearRenameState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameResult?.at]);

  useEffect(() => {
    if (renameSaveError && renameTarget) {
      setRenameError(renameSaveError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renameSaveError]);

  // Measurement-unit dialog ("$/MWh", "kWh", "%"). Works for a single
  // signal (ruler icon on the row) or the whole bulk selection (action
  // bar). Presets + free text — anything can be typed.
  const [unitTarget, setUnitTarget] = useState(null); // {keys, label, current}
  const [unitDraft, setUnitDraft] = useState('');
  const [unitError, setUnitError] = useState(null);

  const openUnitDialog = useCallback((signal) => {
    setUnitTarget({
      keys: [signal.key],
      label: signal.display_name || signal.key,
      current: signal.unit || '',
    });
    setUnitDraft(signal.unit || '');
    setUnitError(null);
  }, []);

  const saveUnit = () => {
    if (!unitTarget || unitTarget.keys.length === 0 || unitSaving) return;
    setUnitError(null);
    saveUnitAssign(unitTarget.keys, unitDraft.trim());
  };

  // Unit assignment saved: update the affected rows locally, bust the
  // cross-surface unit caches, close the dialog.
  useEffect(() => {
    if (!unitResult?.at || !unitTarget) return;
    const unit = unitResult.unit;
    const keySet = new Set(unitResult.keys || []);
    setSignals((prev) => prev.map((s) => (
      keySet.has(s.key)
        ? {
            ...s,
            unit: unit || undefined,
            unit_origin: unit ? 'user' : undefined,
          }
        : s
    )));
    // Charts and driver surfaces cache the workspace unit map — make
    // them refetch on next mount.
    invalidateSignalUnits();
    setUnitTarget(null);
    clearUnitState();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitResult?.at]);

  useEffect(() => {
    if (unitSaveError && unitTarget) {
      setUnitError(unitSaveError);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unitSaveError]);

  // Refresh "which of my signals are published" on mount + after any
  // publish / delist / update mutation lands so the $ icon's color
  // and the manage dialog stay in sync without a manual reload.
  useEffect(() => {
    if (fetchPublishedSignals) fetchPublishedSignals();
    // Verified-provider / publish-capability state: the cards use it
    // (via useCanOpenPublishFlow) to decide whether to render the
    // publish-to-marketplace affordance at all.
    if (fetchMarketplaceSellerStatus) fetchMarketplaceSellerStatus();
  }, [fetchPublishedSignals, fetchMarketplaceSellerStatus]);
  useEffect(() => {
    if (!marketplaceMutations) return;
    if (marketplaceMutations.isLoading) return;
    if (!marketplaceMutations.action) return;
    // Only refresh after the marketplace operations that change the
    // published state of one of MY signals.
    const refreshAfter = ['create', 'update', 'delete'];
    if (refreshAfter.includes(marketplaceMutations.action)
        && marketplaceMutations.result && fetchPublishedSignals) {
      fetchPublishedSignals();
    }
  }, [marketplaceMutations, fetchPublishedSignals]);

  // Map signal_key -> listing summary for the active workspace, from
  // the BE /marketplace/published-signals endpoint.
  const publishedByKey = useMemo(
    () => publishedSignals?.by_signal_key || {},
    [publishedSignals],
  );

  // Single handler that either opens the publish dialog (signal not
  // listed yet) OR opens the manage-published dialog (signal is
  // already on the marketplace).
  const handleMonetizeClick = useCallback((signal, listing) => {
    if (listing) setManagePublished(listing);
    else setPublishTarget(signal);
  }, []);

  // Detail modal (only used by Compact and Rows views — Default Grid
  // already shows the full SignalCard inline, so a click-through
  // there would be redundant). Stores the full signal object so the
  // modal can keep rendering even if the underlying ``signals``
  // array changes between renders.
  const [openSignalSignal, setOpenSignalSignal] = useState(null);
  const handleOpenSignal = useCallback((p) => setOpenSignalSignal(p), []);

  // The page auto-detects the dataset's natural time range on first
  // load and writes it into ``appliedStart/EndDateTime`` to default
  // the date input. That is NOT a user-applied filter - "show me
  // everything" should not light up the activeFilterCount nor force
  // the filter panel open. Only user clicks on Apply / Clear flip
  // this ref, which gates whether the date range counts as active.
  const userTouchedDatesRef = useRef(false);

  // Track which date range we've already seeded so the auto-detect
  // effect only runs ONCE per (workspace, dataset) - not every time the
  // payload reference changes. Without this guard, hopping tabs and
  // coming back would re-detect from a slightly different payload
  // shape and oscillate between two fetches.
  const autoDetectedRef = useRef(false);

  // Mirror redux into local state.
  useEffect(() => {
    setSignals(payload?.signals || []);
  }, [payload?.signals]);

  // Pagination sync.
  useEffect(() => {
    if (payload?.pagination?.current_page) {
      setPage(payload.pagination.current_page);
    }
  }, [payload?.pagination?.current_page]);

  // ---- latest analysis (deterministic priors only) ------------------------
  // Importance was dropped from the signals views (it came from the LLM
  // workspace analysis, which no longer auto-runs). We still read the
  // latest cached analysis for its DETERMINISTIC data_priors block
  // (drift score, autocorr lag, per-feature flags) used by SignalCard.
  const dataPriors = analysisData?.status === 'completed'
    ? (analysisData?.results?.metadata?.data_priors || null)
    : null;

  // ``skip_refresh`` — this page must NEVER kick off an LLM analysis
  // run as a side effect of being viewed; it only consumes whatever is
  // already cached. LLM runs are owned by the discovery section.
  useEffect(() => {
    signalLatestAnalysisFetch({ skip_refresh: true });
  }, [signalLatestAnalysisFetch]);

  // Live (no polling): the workspace-analysis worker emits
  // `signal.analysis.<status>` on the workspace channel — refetch the latest
  // analysis when one arrives (and on reconnect). Replaces the 1s poll.
  useRealtimeRefresh(
    eventPrefix('signal.analysis.'),
    () => signalLatestAnalysisFetch({ skip_refresh: true }),
  );

  // ---- date filter helpers -----------------------------------------------
  const normalizeDateTimeParam = (v) => (!v ? '' : v.length === 16 ? `${v}:00` : v);
  const toDateTimeLocalValue = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 16);
  };
  const getTimestampMs = (v) => {
    if (!v) return null;
    const t = new Date(v).getTime();
    return Number.isNaN(t) ? null : t;
  };

  const dateFilterQuery = useMemo(() => {
    const params = new URLSearchParams();
    if (appliedStartDateTime) params.set('start_date', normalizeDateTimeParam(appliedStartDateTime));
    if (appliedEndDateTime)   params.set('end_date',   normalizeDateTimeParam(appliedEndDateTime));
    return params.toString();
  }, [appliedStartDateTime, appliedEndDateTime]);

  // Server-side group filter: the BE resolves the group's member keys with
  // the same resolver that annotates rows, so pagination + totals reflect
  // the filtered set (not just the current page).
  const groupFilterQuery = useMemo(() => {
    if (!groupFilter) return '';
    const params = new URLSearchParams();
    params.set('group', groupFilter);
    return params.toString();
  }, [groupFilter]);

  // BE-driven sort + filter params. The FE sends them as part of the
  // GET /signal query so the response arrives pre-sorted and
  // pre-filtered (no client-side reorder once /relevance lands).
  // Sort options that have no ``beSort`` (anomalies) are
  // intentionally NOT sent — they remain client-side fallbacks over
  // whatever order the BE returned.
  const sortQuery = useMemo(() => {
    const params = new URLSearchParams();
    const opt = SORT_OPTIONS.find((o) => o.value === sortBy);
    if (opt?.beSort) {
      params.set('sort_by', opt.beSort);
      if (opt.beOrder) params.set('sort_order', opt.beOrder);
    }
    if (publishedFilter && publishedFilter !== 'all') {
      params.set('published_filter', publishedFilter);
    }
    return params.toString();
  }, [sortBy, publishedFilter]);

  const combinedQuery = useMemo(
    () => [query, dateFilterQuery, sortQuery, groupFilterQuery]
      .filter(Boolean).join('&'),
    [query, dateFilterQuery, sortQuery, groupFilterQuery]
  );

  // Helpers that change sort / published filter AND immediately reset
  // pagination. Kept inline (rather than a useEffect on [sortBy,
  // publishedFilter]) so the next combinedQuery-driven fetch sees the
  // already-reset page in its closure - if we deferred the page reset
  // to a follow-up effect, the first fetch after a sort change would
  // still ask for the previous page.
  const setSortByAndResetPage   = (v) => { setSortBy(v);          setPage(1); };
  const setPublishedFilterAndResetPage = (v) => { setPublishedFilter(v); setPage(1); };

  // Auto-populate the date input fields from the first response.
  //
  // The first GET /signal call goes out with no date window so the
  // BE auto-detects the workspace's true data range from CH and bins
  // the chart correctly. We don't refetch with an explicit date
  // window after that — re-applying the FE-derived range was firing
  // a second /list call that re-bucketed the chart against subtly
  // different boundaries (TZ shift on the toDateTimeLocal round-trip,
  // dropped sub-minute precision) and produced flat / off-grid
  // charts. The first call's response is authoritative; the FE just
  // mirrors the detected min/max into the date inputs so users see
  // the data range and can narrow it manually if they want.
  useEffect(() => {
    if (autoDetectedRef.current) return;
    if (appliedStartDateTime || appliedEndDateTime) return;
    if (!payload?.signals?.length) return;

    const minT = payload.signals.reduce((cur, p) => {
      const t = getTimestampMs(p.min_timestamp);
      if (t === null) return cur;
      if (!cur || t < cur.time) return { time: t, value: p.min_timestamp };
      return cur;
    }, null);
    const maxT = payload.signals.reduce((cur, p) => {
      const t = getTimestampMs(p.max_timestamp);
      if (t === null) return cur;
      if (!cur || t > cur.time) return { time: t, value: p.max_timestamp };
      return cur;
    }, null);

    const sv = toDateTimeLocalValue(minT?.value);
    const ev = toDateTimeLocalValue(maxT?.value);
    if (!sv && !ev) return;

    autoDetectedRef.current = true;
    // Mirror the detected window into the date input fields ONLY -
    // do NOT push it into ``appliedStart/EndDateTime`` (which would
    // change ``combinedQuery`` and trigger a second fetch with a
    // slightly-different window that produces a flat chart). The
    // first fetch is the one we render; the user can hit "Apply
    // dates" if they actually want a tighter window.
    setStartDateTime(sv);
    setEndDateTime(ev);
  }, [payload?.signals, appliedStartDateTime, appliedEndDateTime]);

  // Re-fetch whenever the combined query changes. ``takeLatest`` in
  // the saga cancels any in-flight prior request, so firing this on
  // every combined-query change (including the empty-query bootstrap
  // fetch and the subsequent dated fetch from auto-detect) is safe
  // and produces a single, deterministic final response.
  useEffect(() => {
    signalFetchAll(page, payload?.pagination?.per_page || 12, combinedQuery);
    relevanceSignal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedQuery]);

  // Delete error sync.
  useEffect(() => {
    if (deletedSignalError) {
      setDeleteQueue((q) => {
        const idx = q.indexOf(deletedSignalError.error);
        if (idx === -1) return q;
        const cp = [...q]; cp.splice(idx, 1); return cp;
      });
    }
  }, [deletedSignalError]);

  useEffect(() => {
    if (deletedSignal?.response) {
      setSignals((p) => p.filter((pk) => pk.key !== deletedSignal.response));
      if (refreshOverview) refreshOverview();
    }
  }, [deletedSignal]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- group + bulk action effects ---------------------------------------

  // Group names/counts for the filter + assign autocomplete.
  useEffect(() => {
    if (fetchSignalGroups) fetchSignalGroups();
  }, [fetchSignalGroups]);

  // A group assignment landed: refresh the list (rows re-annotate), the
  // group options (names/counts changed), the overview chart (envelopes
  // regroup), and close the dialog.
  useEffect(() => {
    if (!groupAssignResult?.at) return;
    setGroupDialogOpen(false);
    setGroupDraft('');
    clearSelection();
    signalFetchAll(page, payload?.pagination?.per_page || 12, combinedQuery);
    if (fetchSignalGroups) fetchSignalGroups();
    if (refreshOverview) refreshOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupAssignResult?.at]);

  // If the active group filter's group disappears (e.g. its last members
  // were ungrouped or deleted), drop the filter instead of pinning the
  // user on a permanently-empty list.
  useEffect(() => {
    if (!groupFilter || !groupsPayload?.groups) return;
    const stillExists = groupsPayload.groups.some((g) => g.name === groupFilter);
    if (!stillExists) {
      setGroupFilter('');
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groupsPayload, groupFilter]);

  // A bulk delete landed: drop the rows locally (signals by key, event
  // streams by stream), clear the batch, refresh.
  useEffect(() => {
    if (!bulkDeleteResult?.at) return;
    const goneKeys = new Set(bulkDeleteResult.deleted || []);
    const goneStreams = new Set(bulkDeleteResult.deleted_event_streams || []);
    if (goneKeys.size || goneStreams.size) {
      setSignals((p) => p.filter((s) => !goneKeys.has(s.key)
        && !goneStreams.has(s.stream || s.key)));
    }
    setBulkDeleteDialogOpen(false);
    clearSelection();
    signalFetchAll(page, payload?.pagination?.per_page || 12, combinedQuery);
    if (fetchSignalGroups) fetchSignalGroups();
    if (refreshOverview) refreshOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkDeleteResult?.at]);

  // Event STREAM delete completed: drop the row (matches by stream/key) and
  // refresh the overview chart so the deleted stream disappears there too.
  useEffect(() => {
    if (deletedEventStream) {
      setSignals((p) => p.filter(
        (pk) => pk.key !== deletedEventStream && pk.stream !== deletedEventStream,
      ));
      setDeleteQueue((q) => q.filter((k) => k !== deletedEventStream));
      if (refreshOverview) refreshOverview();
      if (clearEventStreamDelete) clearEventStreamDelete();
    }
  }, [deletedEventStream]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---- filtering / sorting (client-side over the current page) ----------

  const filteredSignals = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (signals || []).filter((p) => {
      if (q) {
        const hay = [
          p.key || '',
          p.display_name || '',
          p.group || '',
          ...(p.value_types || []),
          ...(p.treat_as || []),
        ].join(' ').toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (familyFilter.length > 0) {
        if (!familyFilter.includes(familyOf(p))) return false;
      }
      if (qualityFilter.length > 0) {
        const isPatterned = !!p.seasonality?.seasonality;
        const isAnomalous = hasAnomalies(p);
        const rel = relevancePayload?.[p.key];
        const isHighRel = typeof rel === 'number' && rel >= 0.8;
        const checks = {
          patterned: isPatterned,
          anomalous: isAnomalous,
          high_relevance: isHighRel,
        };
        if (!qualityFilter.every((q) => checks[q])) return false;
      }
      return true;
    });
  }, [signals, search, familyFilter, qualityFilter, relevancePayload]);

  // Most sorts are now BE-driven (see ``sortQuery`` / SORT_OPTIONS) so
  // we get the response already in the right order and don't need to
  // reorder client-side. The one FE-only sort that remains is
  // ``anomalies`` (computed per-signal from quality flags), sorted over
  // the current page only.
  const sortedSignals = useMemo(() => {
    const beHandled = SORT_OPTIONS.find((o) => o.value === sortBy)?.beSort;
    if (beHandled) {
      // BE has already sorted (and paginated) — preserve order.
      return filteredSignals;
    }
    const arr = [...filteredSignals];
    const cmp = (a, b) => {
      if (sortBy === 'anomalies') {
        const ac = hasAnomalies(a) ? 1 : 0;
        const bc = hasAnomalies(b) ? 1 : 0;
        if (bc !== ac) return bc - ac;
        return (b.signal_count || 0) - (a.signal_count || 0);
      }
      return 0;
    };
    arr.sort(cmp);
    return arr;
  }, [filteredSignals, sortBy]);

  // Stable unsubscribe handler for the rows table (identity only changes
  // when the page's rows change, not on selection toggles).
  const handleRowUnsubscribe = useCallback((subscriptionId) => {
    setUnsubscribeTarget({
      subscriptionId,
      signal: sortedSignals.find(
        (sp) => sp.marketplace?.subscription_id === subscriptionId,
      ),
    });
  }, [sortedSignals]);

  // ---- KPI metrics --------------------------------------------------------
  const kpis = useMemo(() => {
    let totalRecords = 0;
    let patternedCount = 0;
    let anomalousCount = 0;
    let highRelevanceCount = 0;
    let firstTs = null;
    let lastTs = null;
    (signals || []).forEach((p) => {
      totalRecords += (p.signal_count || 0);
      if (p.seasonality?.seasonality) patternedCount += 1;
      if (hasAnomalies(p)) anomalousCount += 1;
      const rel = relevancePayload?.[p.key];
      if (typeof rel === 'number' && rel >= 0.8) highRelevanceCount += 1;
      const ts0 = getTimestampMs(p.min_timestamp);
      const ts1 = getTimestampMs(p.max_timestamp);
      if (ts0 !== null && (firstTs === null || ts0 < firstTs)) firstTs = ts0;
      if (ts1 !== null && (lastTs === null || ts1 > lastTs)) lastTs = ts1;
    });
    const durationSeconds = (firstTs !== null && lastTs !== null && lastTs > firstTs)
      ? Math.floor((lastTs - firstTs) / 1000) : 0;
    return {
      totalSignals: signals.length,
      totalRecords,
      durationSeconds,
      patternedCount,
      anomalousCount,
      highRelevanceCount,
      firstTimestamp: firstTs ? new Date(firstTs).toISOString() : null,
      lastTimestamp: lastTs ? new Date(lastTs).toISOString() : null,
    };
  }, [signals, relevancePayload]);

  // ---- handlers -----------------------------------------------------------
  const handlePageChange = (event, value) => {
    setPage(value);
    signalFetchAll(value, payload?.pagination?.per_page || 12, combinedQuery);
  };

  const handleApplyDates = () => {
    userTouchedDatesRef.current = true;
    setAppliedStartDateTime(startDateTime);
    setAppliedEndDateTime(endDateTime);
    setPage(1);
  };
  const handleClearDates = () => {
    userTouchedDatesRef.current = true;
    setStartDateTime('');
    setEndDateTime('');
    setAppliedStartDateTime('');
    setAppliedEndDateTime('');
    setPage(1);
  };

  const toggleFamily = (v) =>
    setFamilyFilter((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);
  const toggleQuality = (v) =>
    setQualityFilter((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]);

  // Auto-detected date range doesn't count - only user-applied date
  // filters should bump activeFilterCount and trigger the toolbar's
  // auto-expand behaviour.
  const activeFilterCount =
    (search ? 1 : 0)
    + (familyFilter.length > 0 ? 1 : 0)
    + (qualityFilter.length > 0 ? 1 : 0)
    + (publishedFilter !== 'all' ? 1 : 0)
    + (groupFilter ? 1 : 0)
    + (
      userTouchedDatesRef.current && (appliedStartDateTime || appliedEndDateTime)
        ? 1 : 0
    );

  const clearAll = () => {
    setSearch('');
    setFamilyFilter([]);
    setQualityFilter([]);
    setPublishedFilter('all');
    setGroupFilter('');
    setPage(1);
    handleClearDates();
  };

  const setGroupFilterAndResetPage = useCallback((v) => {
    setGroupFilter(v);
    setPage(1);
  }, []);

  const handleDelete = useCallback((signalKey) => {
    if (!deleteQueue.includes(signalKey)) {
      // Distinguish an event STREAM from a signal so we hit the right
      // delete path (events live in Mongo, signals in ClickHouse).
      const row = (signals || []).find((s) => s.key === signalKey);
      setDeleteIsEvent(row?.type === 'event');
      setDeleteStreamKey(row?.stream || signalKey);
      setIsDeleteDialogOpen(true);
      setSignalToBeDeleted(signalKey);
    }
  }, [deleteQueue, signals]);
  const confirmDelete = () => {
    if (deleteIsEvent) {
      deleteEventStream(deleteStreamKey);
    } else {
      deleteSignal(signalToBeDeleted, false);
    }
    setDeleteQueue((q) => [...q, signalToBeDeleted]);
    setIsDeleteDialogOpen(false);
  };
  const cancelDelete = () => {
    setSignalToBeDeleted(null);
    setIsDeleteDialogOpen(false);
  };

  // ----- marketplace cascade-delete confirm -------------------------------
  // The BE rejects DELETE /signal with 409 when the signal is on the
  // marketplace with active subscribers. The saga surfaces those
  // details via state.signal_delete.marketplaceConflict; we open a
  // second dialog explaining the impact + offering "Delist &
  // Delete" which re-issues the delete with ``force=true`` (cascade
  // cancels every sub on the BE).
  const cascadeConflict = signalDeleteMarketplaceConflict;
  const dismissCascade = () => {
    if (clearSignalDeleteMarketplaceConflict) {
      clearSignalDeleteMarketplaceConflict();
    }
  };
  const confirmCascadeDelete = () => {
    if (!cascadeConflict?.signal_type) {
      dismissCascade();
      return;
    }
    deleteSignal(cascadeConflict.signal_type, true);
    setDeleteQueue((q) =>
      q.includes(cascadeConflict.signal_type)
        ? q
        : [...q, cascadeConflict.signal_type]);
    dismissCascade();
  };

  // ---- render -------------------------------------------------------------

  // Memoized so its element identity only changes when a filter input
  // changes — otherwise every unrelated state change (e.g. a selection
  // checkbox) would hand SignalsOverviewChart + ListingToolbar a fresh
  // ``filtersSlot`` element and force them to re-render too.
  const filtersNode = useMemo(() => (
    <>
      <TextField
        size="small"
        placeholder="Search signals by name or type…"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.secondary' }} />,
        }}
        sx={{ minWidth: 240, flex: 1 }}
      />
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Family
        </Typography>
        {FAMILY_FILTER_OPTIONS.map((opt) => {
          const selected = familyFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleFamily(opt.value)}
              color={selected ? 'primary' : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Quality
        </Typography>
        {QUALITY_FILTER_OPTIONS.map((opt) => {
          const selected = qualityFilter.includes(opt.value);
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => toggleQuality(opt.value)}
              color={selected ? opt.color : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="caption" color="text.secondary" sx={{
          fontWeight: 600, letterSpacing: 0.4, textTransform: 'uppercase', mr: 0.5,
        }}>
          Marketplace
        </Typography>
        {/* Mutually-exclusive chip pair. Clicking the active chip
            again clears back to "all". Sent to the BE via
            ``published_filter`` so pagination + counts stay accurate. */}
        {PUBLISHED_FILTER_OPTIONS.map((opt) => {
          const selected = publishedFilter === opt.value;
          return (
            <Chip
              key={opt.value}
              label={opt.label}
              size="small"
              onClick={() => setPublishedFilterAndResetPage(
                selected ? 'all' : opt.value,
              )}
              color={selected ? opt.color : 'default'}
              variant={selected ? 'filled' : 'outlined'}
              sx={{ cursor: 'pointer' }}
            />
          );
        })}
      </Stack>
      {groupOptions.length > 0 && (
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <InputLabel>Group</InputLabel>
          <Select
            value={groupFilter}
            label="Group"
            onChange={(e) => setGroupFilterAndResetPage(e.target.value)}
          >
            <MenuItem value="" sx={{ fontSize: 13 }}>
              <em>All groups</em>
            </MenuItem>
            {(groupsPayload?.groups || []).map((g) => (
              <MenuItem key={g.name} value={g.name} sx={{ fontSize: 13 }}>
                {g.name}&nbsp;
                <Typography component="span" variant="caption" color="text.secondary">
                  ({g.count})
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems="center">
        <TextField
          label="From"
          type="datetime-local"
          size="small"
          value={startDateTime}
          onChange={(e) => setStartDateTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200 }}
        />
        <TextField
          label="To"
          type="datetime-local"
          size="small"
          value={endDateTime}
          onChange={(e) => setEndDateTime(e.target.value)}
          InputLabelProps={{ shrink: true }}
          sx={{ minWidth: 200 }}
        />
        <Button size="small" variant="contained" onClick={handleApplyDates}>Apply dates</Button>
        <Tooltip title="Reset date range" arrow placement="top">
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={handleClearDates}
            startIcon={<RestartAltIcon />}
          >
            Reset
          </Button>
        </Tooltip>
      </Stack>
    </>
    // ``handleApplyDates`` closes over startDateTime/endDateTime, which are
    // deps here, so the memoized closure is never stale. The remaining
    // handlers only call state setters (stable identities).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), [search, familyFilter, qualityFilter, publishedFilter, groupFilter,
      groupsPayload, groupOptions, startDateTime, endDateTime]);

  // Right-side action area for the toolbar: view-mode toggle (3
  // options) sits IMMEDIATELY LEFT of the Sort select so the two
  // controls read as one cluster. The toggle persists the user's
  // choice in localStorage (see ``persistViewMode``).
  const sortNode = (
    <Stack direction="row" spacing={1} alignItems="center">
      <Tooltip title="Layout">
        <ToggleButtonGroup
          size="small"
          value={viewMode}
          exclusive
          onChange={(_, next) => { if (next) setViewMode(next); }}
          sx={{
            '& .MuiToggleButton-root': {
              padding: '4px 8px',
              border: `1px solid ${theme.palette.divider}`,
            },
          }}
        >
          <ToggleButton value="compact" aria-label="Compact view">
            <Tooltip title="Compact (6 per row)">
              <AppsIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="grid" aria-label="Grid view">
            <Tooltip title="Grid (default)">
              <GridViewIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
          <ToggleButton value="rows" aria-label="Rows view">
            <Tooltip title="Rows (table)">
              <ViewListIcon fontSize="small" />
            </Tooltip>
          </ToggleButton>
        </ToggleButtonGroup>
      </Tooltip>
      <FormControl size="small" sx={{ minWidth: 220 }}>
        <InputLabel>Sort</InputLabel>
        <Select
          value={sortBy}
          label="Sort"
          onChange={(e) => setSortByAndResetPage(e.target.value)}
        >
          {SORT_OPTIONS.map((o) => (
            <MenuItem key={o.value} value={o.value} sx={{ fontSize: 13 }}>
              {o.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Stack>
  );

  // Loading state on first load — shimmer matches the persisted
  // view mode so the layout doesn't jump when real data lands.
  if (isLoading && signals.length === 0) {
    let skeletonBody;
    if (viewMode === 'rows') {
      skeletonBody = <SkeletonRowsTable rows={8} />;
    } else if (viewMode === 'compact') {
      skeletonBody = (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'repeat(2, 1fr)',
            sm: 'repeat(3, 1fr)',
            md: 'repeat(5, 1fr)',
            lg: 'repeat(6, 1fr)',
            xl: 'repeat(8, 1fr)',
          },
          gap: 1,
        }}>
          {Array.from({ length: 12 }).map((_, i) => (
            <SkeletonCompactCard key={i} />
          ))}
        </Box>
      );
    } else {
      skeletonBody = (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)',
          },
          gap: 2,
        }}>
          {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
        </Box>
      );
    }
    return (
      <Box sx={{ width: '100%' }}>
        <SignalsKpiStrip metrics={kpis} />
        <ListingToolbar filters={filtersNode} actions={sortNode} activeCount={0} />
        {skeletonBody}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, bgcolor: alpha(theme.palette.error.main, 0.08), color: theme.palette.error.main, borderRadius: 1 }}>
        <Typography variant="h6">Failed to load signals</Typography>
        <Typography variant="body2">{String(error)}</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%' }}>
      <SignalsKpiStrip metrics={kpis} />

      <ListingToolbar
        filters={filtersNode}
        actions={sortNode}
        activeCount={activeFilterCount}
        onClearFilters={clearAll}
        trailing={
          activeFilterCount > 0
            ? `${sortedSignals.length} of ${signals.length}`
            : null
        }
      />

      {/* Workspace overview: all signals + event streams on one time chart.
          Mirrors the active filters (search / dates / family / published).
          ``filtersSlot`` surfaces the same filter controls inside the chart's
          full-screen view. */}
      <SignalsOverviewChart filterQuery={combinedQuery} filtersSlot={filtersNode} />

      {sortedSignals.length === 0 ? (
        <Box sx={{
          py: 8,
          textAlign: 'center',
          color: theme.palette.text.secondary,
          border: `1px dashed ${theme.palette.divider}`,
          borderRadius: 2,
        }}>
          <InsightsIcon sx={{ fontSize: 36, opacity: 0.5, mb: 1 }} />
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {activeFilterCount > 0 ? 'No signals match your filters' : 'No signals on this page'}
          </Typography>
          <Typography variant="body2" sx={{ mt: 0.5 }}>
            {activeFilterCount > 0
              ? 'Try a different keyword or clear the family / quality filters.'
              : 'Once data flows in, every column becomes a signal you can analyse here.'}
          </Typography>
        </Box>
      ) : viewMode === 'rows' ? (
        // ROWS view — same DataTable shell used by Trainings. Whole
        // row click opens the SignalDetailModal (which renders the
        // full SignalCard at dialog width). Rows view is also the
        // MANAGE view: checkbox selection + a bulk-actions bar for
        // group assignment and batch delete.
        <>
          {selected.size > 0 && (
            // Floating action bar: fixed to the bottom of the viewport so
            // selecting a row never shifts the layout, and the actions stay
            // reachable while scrolled deep into the table. Sits below
            // dialogs/snackbars in the z-order.
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{
                position: 'fixed',
                bottom: 24,
                left: '50%',
                transform: 'translateX(-50%)',
                zIndex: theme.zIndex.appBar + 1,
                px: 2, py: 1,
                border: `1px solid ${alpha(theme.palette.primary.main, 0.45)}`,
                bgcolor: theme.palette.background.paper,
                borderRadius: 99,
                boxShadow: theme.palette.boxShadow
                  || '0 8px 24px rgba(0,0,0,0.35)',
                whiteSpace: 'nowrap',
                '@keyframes bulkbar-in': {
                  from: {
                    opacity: 0,
                    transform: 'translateX(-50%) translateY(12px)',
                  },
                  to: {
                    opacity: 1,
                    transform: 'translateX(-50%) translateY(0)',
                  },
                },
                animation: 'bulkbar-in 160ms ease-out',
              }}
            >
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {selected.size} selected
                {selectedEventCount > 0 && (
                  <Typography component="span" variant="caption"
                              color="text.secondary" sx={{ ml: 0.5 }}>
                    ({selectedEventCount} event
                    {selectedEventCount === 1 ? '' : 's'})
                  </Typography>
                )}
              </Typography>
              <Tooltip title={selectedEventCount > 0
                ? 'Event streams can\u2019t be grouped — deselect them to enable group actions'
                : ''}>
                <span>
                  <Button
                    size="small"
                    startIcon={<LabelOutlinedIcon />}
                    onClick={() => setGroupDialogOpen(true)}
                    disabled={groupAssignLoading || selectedEventCount > 0}
                  >
                    Add to group
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={selectedEventCount > 0
                ? 'Event streams can\u2019t be grouped — deselect them to enable group actions'
                : 'Remove explicit group assignments — the signals revert to automatic grouping'}>
                <span>
                  <Button
                    size="small"
                    startIcon={<LabelOffOutlinedIcon />}
                    onClick={() => assignGroup({
                      mode: 'unassign', keys: selectedSignalKeys,
                    })}
                    disabled={groupAssignLoading || selectedEventCount > 0}
                  >
                    Ungroup
                  </Button>
                </span>
              </Tooltip>
              <Tooltip title={selectedEventCount > 0
                ? 'Event streams have no measurement unit — deselect them to set units'
                : 'Set the measurement unit (e.g. $/MWh, kWh, %) for the selected signals'}>
                <span>
                  <Button
                    size="small"
                    startIcon={<StraightenOutlinedIcon />}
                    onClick={() => {
                      setUnitTarget({
                        keys: selectedSignalKeys,
                        label: `${selectedSignalKeys.length} signal${selectedSignalKeys.length === 1 ? '' : 's'}`,
                        current: '',
                      });
                      setUnitDraft('');
                      setUnitError(null);
                    }}
                    disabled={unitSaving || selectedEventCount > 0}
                  >
                    Set unit
                  </Button>
                </span>
              </Tooltip>
              <Button
                size="small"
                color="error"
                startIcon={<DeleteOutlineIcon />}
                onClick={() => setBulkDeleteDialogOpen(true)}
                disabled={bulkDeleteLoading}
              >
                Delete
              </Button>
              <Box sx={{
                width: '1px', alignSelf: 'stretch', my: 0.5,
                bgcolor: theme.palette.divider,
              }} />
              <Button
                size="small"
                color="inherit"
                startIcon={<CloseIcon />}
                onClick={clearSelection}
              >
                Clear
              </Button>
            </Stack>
          )}
          <SignalsRowsTable
            signals={sortedSignals}
            relevance={relevancePayload}
            publishedByKey={publishedByKey}
            deletingKeys={deleteQueue}
            selectedKeys={selectedKeySet}
            onToggleSelect={toggleSelect}
            onToggleSelectPage={toggleSelectPage}
            onGroupClick={setGroupFilterAndResetPage}
            onRecordEvent={onRecordEvent}
            onOpen={handleOpenSignal}
            onDelete={handleDelete}
            onPublish={handleMonetizeClick}
            onRename={openRename}
            onSetUnit={openUnitDialog}
            onUnsubscribe={handleRowUnsubscribe}
          />
        </>
      ) : (
        // GRID and COMPACT both use a CSS grid - column count differs.
        // Compact targets 6/row at lg, 4/row at md, 2/row at xs.
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: viewMode === 'compact'
              ? {
                  xs: 'repeat(2, 1fr)',
                  sm: 'repeat(3, 1fr)',
                  md: 'repeat(5, 1fr)',
                  lg: 'repeat(6, 1fr)',
                  xl: 'repeat(8, 1fr)',
                }
              : {
                  xs: '1fr',
                  md: 'repeat(2, 1fr)',
                  lg: 'repeat(3, 1fr)',
                },
            gap: viewMode === 'compact' ? 1 : 2,
          }}
        >
          {sortedSignals.map((signal) => {
            // While a delete is in flight (request fired but the BE
            // hasn't acked yet), dim the card and disable pointer
            // events so the seller can't click Delete a second time
            // and so they get an obvious visual cue that something
            // is happening. The wrapper approach keeps the leaf
            // SignalCard / CompactSignalCard agnostic of the queue.
            const isDeleting = deleteQueue.includes(signal.key);
            const wrapper = (
              <Box
                key={signal.key}
                sx={{
                  position: 'relative',
                  opacity: isDeleting ? 0.45 : 1,
                  pointerEvents: isDeleting ? 'none' : 'auto',
                  transition: 'opacity 120ms ease-in-out',
                }}
              >
                {isDeleting && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 8, right: 8,
                      zIndex: 2,
                      display: 'flex', alignItems: 'center', gap: 0.5,
                      px: 1, py: 0.25,
                      bgcolor: theme.palette.background.paper,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      boxShadow: theme.palette.boxShadow,
                    }}
                  >
                    <CircularProgress size={12} />
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      Deleting…
                    </Typography>
                  </Box>
                )}
                {viewMode === 'compact' ? (
                  <CompactSignalCard
                    signal={signal}
                    relevance={relevancePayload}
                    publishedListing={publishedByKey[signal.key] || null}
                    onOpen={(p) => setOpenSignalSignal(p)}
                    onDelete={handleDelete}
                    onPublish={handleMonetizeClick}
                    onUnsubscribe={(subscriptionId) =>
                      setUnsubscribeTarget({ subscriptionId, signal })}
                  />
                ) : (
                  <SignalCard
                    signal={signal}
                    relevance={relevancePayload}
                    dataPriors={dataPriors}
                    publishedListing={publishedByKey[signal.key] || null}
                    onDelete={handleDelete}
                    onPublish={handleMonetizeClick}
                    onRename={openRename}
                    onUnsubscribe={(subscriptionId) =>
                      setUnsubscribeTarget({ subscriptionId, signal })}
                  />
                )}
              </Box>
            );
            return wrapper;
          })}
        </Box>
      )}

      {count > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={count}
            page={page}
            onChange={handlePageChange}
            shape="rounded"
            color="primary"
          />
        </Box>
      )}

      {/* Add-to-group dialog: assigns every selected key to a group.
          Free text creates a new group; the autocomplete offers the
          workspace's existing (resolved) group names. */}
      <Dialog
        open={groupDialogOpen}
        onClose={() => !groupAssignLoading && setGroupDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Add {selected.size} signal{selected.size === 1 ? '' : 's'} to a group</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Groups organize the signals list and the overview chart. Pick an
            existing group or type a new name. Your assignment overrides the
            automatic grouping and can be removed anytime with “Ungroup”.
          </DialogContentText>
          <Autocomplete
            freeSolo
            options={groupOptions}
            value={groupDraft}
            onInputChange={(_, v) => setGroupDraft(v || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                label="Group"
                placeholder="e.g. Store ops"
                inputProps={{ ...params.inputProps, maxLength: 60 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && groupDraft.trim()) {
                    e.preventDefault();
                    assignGroup({
                      mode: 'assign', keys: selectedSignalKeys,
                      group: groupDraft.trim(),
                    });
                  }
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CMButtonError
            startIcon={<CancelIcon />}
            onClick={() => setGroupDialogOpen(false)}
            disabled={groupAssignLoading}
          >
            Cancel
          </CMButtonError>
          <CMButtonSuccess
            startIcon={groupAssignLoading
              ? <CircularProgress size={14} color="inherit" />
              : <CheckIcon />}
            onClick={() => assignGroup({
              mode: 'assign', keys: selectedSignalKeys,
              group: groupDraft.trim(),
            })}
            disabled={groupAssignLoading || !groupDraft.trim()}
          >
            Add to group
          </CMButtonSuccess>
        </DialogActions>
      </Dialog>

      {/* Bulk delete confirm. Subscribed marketplace rows in the batch are
          skipped by the BE (they're cancelled from the Marketplace tab). */}
      <Dialog
        open={bulkDeleteDialogOpen}
        onClose={() => !bulkDeleteLoading && setBulkDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          Delete {[
            selectedSignalKeys.length > 0
              && `${selectedSignalKeys.length} signal${selectedSignalKeys.length === 1 ? '' : 's'}`,
            selectedEventCount > 0
              && `${selectedEventCount} event stream${selectedEventCount === 1 ? '' : 's'}`,
          ].filter(Boolean).join(' and ')}?
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This drops {[
              selectedSignalKeys.length > 0
                && 'all records of the selected signal types',
              selectedEventCount > 0
                && 'every event in the selected streams',
            ].filter(Boolean).join(' and ')} and cannot be undone.
            Marketplace-subscribed items in the selection are skipped —
            cancel those subscriptions from the Marketplace tab.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CMButtonError
            startIcon={<CancelIcon />}
            onClick={() => setBulkDeleteDialogOpen(false)}
            disabled={bulkDeleteLoading}
          >
            Cancel
          </CMButtonError>
          <CMButtonSuccess
            startIcon={bulkDeleteLoading
              ? <CircularProgress size={14} color="inherit" />
              : <CheckIcon />}
            onClick={() => bulkDelete(
              selectedSignalKeys, selectedEventStreams, false,
            )}
            disabled={bulkDeleteLoading}
            autoFocus
          >
            Delete
          </CMButtonSuccess>
        </DialogActions>
      </Dialog>

      {/* Bulk marketplace-conflict confirm (batch flavor of the cascade
          dialog): some selected signals are published listings. "Delist &
          Delete" re-sends the batch with force=true. */}
      <Dialog
        open={!!bulkDeleteConflict}
        onClose={clearBulkDeleteConflict}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="span" sx={{
            color: (bulkDeleteConflict?.active_subscribers || 0) > 0
              ? 'warning.main' : 'info.main',
            display: 'flex',
          }}>
            {(bulkDeleteConflict?.active_subscribers || 0) > 0 ? '⚠' : 'ⓘ'}
          </Box>
          Some selected signals are on the marketplace
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            <Box sx={{ mb: 1.5 }}>{bulkDeleteConflict?.message}</Box>
            {(bulkDeleteConflict?.listings || []).length > 0 && (
              <Box sx={{
                mt: 1, mb: 1.5, p: 1,
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 1, bgcolor: 'background.default',
                maxHeight: 160, overflowY: 'auto',
              }}>
                {(bulkDeleteConflict?.listings || []).map((l) => (
                  <Box key={l.listing_id} sx={{
                    display: 'flex', justifyContent: 'space-between',
                    gap: 2, py: 0.25,
                  }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {l.title || l.listing_id}
                      <Typography component="span" variant="caption"
                                  color="text.secondary" sx={{ ml: 0.5 }}>
                        ({l.signal_key})
                      </Typography>
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {l.subscribers || 0} subscriber
                      {l.subscribers === 1 ? '' : 's'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            )}
            Continuing will <strong>delist the listings</strong>
            {(bulkDeleteConflict?.active_subscribers || 0) > 0 && (
              <> and <strong>cancel every subscription</strong></>
            )}
            , then delete the signals. This <strong>cannot be undone</strong>.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CMButtonError startIcon={<CancelIcon />} onClick={clearBulkDeleteConflict}>
            Keep listings live
          </CMButtonError>
          <CMButtonSuccess
            startIcon={<CheckIcon />}
            onClick={() => {
              const keys = bulkDeleteConflict?.keys || selectedSignalKeys;
              const streams = bulkDeleteConflict?.streams
                || selectedEventStreams;
              clearBulkDeleteConflict();
              bulkDelete(keys, streams, true);
            }}
            autoFocus
          >
            Delist &amp; Delete
          </CMButtonSuccess>
        </DialogActions>
      </Dialog>

      {/* Rename (display name) dialog — sets the friendly name shown
          across the platform; the underlying signal key never changes. */}
      <Dialog
        open={!!renameTarget}
        onClose={() => !renameSaving && setRenameTarget(null)}
        aria-labelledby="rename-signal-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="rename-signal-title">Rename signal</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Set a friendly display name for{' '}
            <strong>{renameTarget?.key}</strong>. It will be used everywhere
            on the platform — charts, segments, trainings and forms. The
            underlying key stays the same. Leave empty to show the raw key.
          </DialogContentText>
          <TextField
            autoFocus
            fullWidth
            size="small"
            label="Display name"
            placeholder={renameTarget?.key}
            value={renameDraft}
            onChange={(e) => setRenameDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') saveRename(); }}
            error={!!renameError}
            helperText={renameError || 'e.g. rename "o" to "Open"'}
            inputProps={{ maxLength: 120 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CMButtonError
            startIcon={<CancelIcon />}
            onClick={() => setRenameTarget(null)}
            disabled={renameSaving}
          >
            Cancel
          </CMButtonError>
          <CMButtonSuccess
            startIcon={renameSaving
              ? <CircularProgress size={14} color="inherit" />
              : <CheckIcon />}
            onClick={saveRename}
            disabled={renameSaving}
          >
            Save
          </CMButtonSuccess>
        </DialogActions>
      </Dialog>

      {/* Measurement-unit dialog — presets grouped by domain, free text
          for anything else. Applies to one signal or the bulk selection;
          leaving it empty clears the unit (reverts to LLM-inferred). */}
      <Dialog
        open={!!unitTarget}
        onClose={() => !unitSaving && setUnitTarget(null)}
        aria-labelledby="unit-signal-title"
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle id="unit-signal-title">
          Set measurement unit
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            The unit for <strong>{unitTarget?.label}</strong> is shown
            everywhere values appear — charts, tooltips, drivers and
            simulations. Pick a preset or type your own. Leave empty to
            clear it.
          </DialogContentText>
          <Autocomplete
            freeSolo
            options={UNIT_PRESET_OPTIONS}
            groupBy={(o) => (typeof o === 'string' ? '' : o.group)}
            getOptionLabel={(o) => (typeof o === 'string' ? o : o.value)}
            inputValue={unitDraft}
            onInputChange={(_, v) => setUnitDraft(v || '')}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                size="small"
                label="Unit"
                placeholder="e.g. $/MWh, kWh, %"
                error={!!unitError}
                helperText={unitError
                  || (unitTarget?.current
                    ? `Current: ${unitTarget.current}`
                    : 'Presets below, or type any unit')}
                inputProps={{ ...params.inputProps, maxLength: 24 }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') { e.preventDefault(); saveUnit(); }
                }}
              />
            )}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CMButtonError
            startIcon={<CancelIcon />}
            onClick={() => setUnitTarget(null)}
            disabled={unitSaving}
          >
            Cancel
          </CMButtonError>
          <CMButtonSuccess
            startIcon={unitSaving
              ? <CircularProgress size={14} color="inherit" />
              : <CheckIcon />}
            onClick={saveUnit}
            disabled={unitSaving}
          >
            Save
          </CMButtonSuccess>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isDeleteDialogOpen}
        onClose={cancelDelete}
        aria-labelledby="delete-signal-title"
      >
        <DialogTitle id="delete-signal-title">Delete Signal Type</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{signalToBeDeleted}</strong>? This will drop
            all signals of this type and cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CMButtonError startIcon={<CancelIcon />} onClick={cancelDelete}>
            Cancel
          </CMButtonError>
          <CMButtonSuccess startIcon={<CheckIcon />} onClick={confirmDelete} autoFocus>
            Confirm
          </CMButtonSuccess>
        </DialogActions>
      </Dialog>

      {/* Marketplace cascade-delete confirmation. Opens automatically
          when the BE returns 409 because the signal is published with
          active subscribers. "Delist & Delete" re-calls the same
          endpoint with force=true; on the BE that cascade-cancels
          every active subscription, marks the listing(s) delisted,
          then drops the signal. */}
      {(() => {
        const isAdvisory = cascadeConflict?.kind === 'published_marketplace_listing';
        const subs = cascadeConflict?.active_subscribers || 0;
        const listingsArr = cascadeConflict?.listings || [];
        return (
          <Dialog
            open={!!cascadeConflict}
            onClose={dismissCascade}
            aria-labelledby="delete-signal-cascade-title"
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              id="delete-signal-cascade-title"
              sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
            >
              <Box
                component="span"
                sx={{
                  color: isAdvisory ? 'info.main' : 'warning.main',
                  display: 'flex',
                }}
              >
                {isAdvisory ? 'ⓘ' : '⚠'}
              </Box>
              {isAdvisory
                ? 'This signal is on the marketplace'
                : 'This signal has active marketplace subscribers'}
            </DialogTitle>
            <DialogContent>
              <DialogContentText component="div">
                <Box sx={{ mb: 1.5 }}>
                  {isAdvisory ? (
                    <>
                      <strong>{cascadeConflict?.signal_type}</strong> is
                      currently published as{' '}
                      <strong>{listingsArr.length}</strong> marketplace
                      listing{listingsArr.length === 1 ? '' : 's'}. Nobody
                      is subscribed yet.
                    </>
                  ) : (
                    <>
                      <strong>{cascadeConflict?.signal_type}</strong>{' '}
                      currently has <strong>{subs}</strong> active
                      subscriber{subs === 1 ? '' : 's'} across{' '}
                      {listingsArr.length} listing
                      {listingsArr.length === 1 ? '' : 's'}.
                    </>
                  )}
                </Box>
                {listingsArr.length > 0 && (
                  <Box
                    sx={{
                      mt: 1,
                      mb: 1.5,
                      p: 1,
                      border: `1px solid ${theme.palette.divider}`,
                      borderRadius: 1,
                      bgcolor: 'background.default',
                      maxHeight: 160,
                      overflowY: 'auto',
                    }}
                  >
                    {listingsArr.map((l) => (
                      <Box
                        key={l.listing_id}
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          gap: 2,
                          py: 0.25,
                        }}
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {l.title || l.listing_id}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {l.subscribers || 0} subscriber
                          {l.subscribers === 1 ? '' : 's'}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                )}
                {isAdvisory ? (
                  <>
                    Continuing will{' '}
                    <strong>delist {listingsArr.length === 1 ? 'it' : 'them'} from
                    the marketplace</strong> and then delete the underlying
                    signal. No buyers will be affected. This{' '}
                    <strong>cannot be undone</strong>.
                  </>
                ) : (
                  <>
                    Continuing will{' '}
                    <strong>delist every listing</strong> for this signal
                    and <strong>cancel every subscription</strong>{' '}
                    immediately. Buyers will stop being charged starting
                    today and their segments will lose access to this data
                    on their next refresh. This{' '}
                    <strong>cannot be undone</strong>.
                  </>
                )}
              </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2.5 }}>
              <CMButtonError startIcon={<CancelIcon />} onClick={dismissCascade}>
                Keep listing live
              </CMButtonError>
              <CMButtonSuccess
                startIcon={<CheckIcon />}
                onClick={confirmCascadeDelete}
                autoFocus
              >
                {isAdvisory ? 'Delist & Delete' : 'Delist & Delete'}
              </CMButtonSuccess>
            </DialogActions>
          </Dialog>
        );
      })()}

      {/* Click-through detail modal — used by Compact and Rows
          views (the inline Default Grid card already shows full
          detail, so it doesn't need this). Renders the same
          SignalCard at dialog width so the chart actually has room
          to breathe. */}
      <SignalDetailModal
        open={!!openSignalSignal}
        signal={openSignalSignal}
        relevance={relevancePayload}
        dataPriors={dataPriors}
        publishedListing={openSignalSignal
          ? (publishedByKey[openSignalSignal.key] || null)
          : null}
        onClose={() => setOpenSignalSignal(null)}
        onDelete={handleDelete}
        onPublish={handleMonetizeClick}
        onRename={openRename}
        onUnsubscribe={(subscriptionId) =>
          setUnsubscribeTarget({
            subscriptionId,
            signal: openSignalSignal,
          })}
      />

      {/* Marketplace publish dialog: 5-step wizard, opened from a
          SignalCard's "Publish to Marketplace" affordance. The
          dialog dispatches its own Redux actions; we just provide
          the seed signal_key + data_type and close on success. */}
      <PublishListingDialog
        open={!!publishTarget}
        signalKey={
          publishTarget?.type === 'event'
            ? (publishTarget?.stream || publishTarget?.source || publishTarget?.key)
            : publishTarget?.key
        }
        listingType={publishTarget?.type === 'event' ? 'event' : 'signal'}
        dataType={
          (publishTarget?.treat_as && publishTarget.treat_as[0])
          || (publishTarget?.value_types && publishTarget.value_types[0])
          || null
        }
        onClose={() => setPublishTarget(null)}
      />

      {/* Manage-published action sheet — shown when the user clicks
          the storefront icon on a signal that's already on the
          marketplace. "Edit" navigates to the standalone full-page
          edit screen (no longer a wizard); "Delist" still routes
          through the manage dialog. */}
      <ManagePublishedDialog
        open={!!managePublished}
        listing={managePublished}
        onClose={() => setManagePublished(null)}
        onEdit={(listing) => {
          setManagePublished(null);
          if (listing?.listing_id) {
            navigate(`/marketplace/listings/${listing.listing_id}/edit`);
          }
        }}
      />

      {/* Marketplace unsubscribe confirm */}
      <Dialog
        open={!!unsubscribeTarget}
        onClose={() => setUnsubscribeTarget(null)}
      >
        <DialogTitle>Unsubscribe from this signal?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>
              {unsubscribeTarget?.signal?.marketplace?.listing_title
                || unsubscribeTarget?.signal?.key}
            </strong>{' '}
            will be removed from this workspace at the next daily billing
            tick. <strong>Today is still charged in full.</strong>
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <CMButtonError onClick={() => setUnsubscribeTarget(null)}>
            Keep subscription
          </CMButtonError>
          <CMButtonSuccess
            onClick={() => {
              cancelMarketplaceSubscription(unsubscribeTarget.subscriptionId);
              setUnsubscribeTarget(null);
            }}
            autoFocus
          >
            Unsubscribe
          </CMButtonSuccess>
        </DialogActions>
      </Dialog>

      {(isLoading && signals.length > 0) && (
        <Box sx={{
          position: 'fixed',
          right: 24, bottom: 24,
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 2,
          px: 1.5, py: 0.75,
          display: 'flex', alignItems: 'center', gap: 1,
          boxShadow: theme.palette.boxShadow,
          zIndex: 4,
        }}>
          <CircularProgress size={14} />
          <Typography variant="caption" color="text.secondary">Refreshing…</Typography>
        </Box>
      )}
    </Box>
  );
});

const mapStateToProps = (state) => ({
  payload: state.signal_list.response,
  isLoading: state.signal_list.isLoading,
  error: state.signal_list.error,
  relevancePayload: state.signal_relevance.response,
  relevanceLoading: state.signal_relevance.isLoading,
  relevanceError: state.signal_relevance.error,
  deletedSignal: state.signal_delete.response,
  deletedSignalError: state.signal_delete.error,
  signalDeleteMarketplaceConflict: state.signal_delete.marketplaceConflict,
  deletedEventStream: state.event_stream_delete.response,
  analysisData: state.signal_latest_analysis.response,
  analysisError: state.signal_latest_analysis.error,
  // Marketplace: which of MY signals are already published in this
  // workspace + the shared mutation channel (so we can refetch this
  // map after a publish / update / delist completes).
  publishedSignals: state.marketplace_published_signals.response,
  marketplaceMutations: state.marketplace_mutations,
  // Group management + bulk delete
  groupsPayload: state.signal_groups.response,
  groupAssignResult: state.signal_group_assign.response,
  groupAssignLoading: state.signal_group_assign.isLoading,
  bulkDeleteResult: state.signal_delete_bulk.response,
  bulkDeleteLoading: state.signal_delete_bulk.isLoading,
  bulkDeleteConflict: state.signal_delete_bulk.marketplaceConflict,
  // Rename (display name) + measurement unit assignment
  renameSaving: state.signal_display_name.isLoading,
  renameResult: state.signal_display_name.response,
  renameSaveError: state.signal_display_name.error,
  unitSaving: state.signal_assign_unit.isLoading,
  unitResult: state.signal_assign_unit.response,
  unitSaveError: state.signal_assign_unit.error,
});

const mapDispatchToProps = (dispatch) => ({
  signalFetchAll: (page, rowsPerPage, query) =>
    dispatch(signalFetchAll({ page, rowsPerPage, query })),
  relevanceSignal: () => dispatch(signalRelevance()),
  deleteSignal: (signal_type, force = false) =>
    dispatch(signalDelete({ signal_type, force })),
  deleteEventStream: (stream) => dispatch(eventStreamDelete({ stream })),
  clearEventStreamDelete: () => dispatch(clearEventStreamDelete()),
  refreshOverview: () => dispatch(overviewFetch()),
  clearSignalDeleteMarketplaceConflict: () =>
    dispatch(clearSignalDeleteMarketplaceConflictAction()),
  signalLatestAnalysisFetch: (payload) => dispatch(signalLatestAnalysisFetch(payload)),
  cancelMarketplaceSubscription: (subscriptionId) =>
    dispatch(cancelSubscribe(subscriptionId)),
  fetchPublishedSignals: () => dispatch(fetchPublishedSignals()),
  fetchMarketplaceSellerStatus: () => dispatch(fetchSellerStatus()),
  fetchSignalGroups: () => dispatch(signalGroupsFetch()),
  assignGroup: (payload) => dispatch(signalGroupAssign(payload)),
  bulkDelete: (keys, streams, force) =>
    dispatch(signalDeleteBulk({ keys, streams, force })),
  clearBulkDeleteConflict: () => dispatch(clearBulkDeleteConflictAction()),
  saveDisplayName: (key, display_name) =>
    dispatch(signalDisplayNameRequest({ key, display_name })),
  clearRenameState: () => dispatch(signalDisplayNameClear()),
  saveUnitAssign: (keys, unit) =>
    dispatch(signalAssignUnitRequest({ keys, unit })),
  clearUnitState: () => dispatch(signalAssignUnitClear()),
});

export default connect(mapStateToProps, mapDispatchToProps)(SignalList);
